import stringifySafe from 'json-stringify-safe';
import { pricingPlansClientAuditLogSrc92Evid408 } from '@wix/bi-logger-pricing-plans-data/v2';
import { HttpClient } from '@wix/http-client';
import { OwnerLogger, VisitorLogger } from '@wix/yoshi-flow-editor';
import { BenefitBulletType, UserData } from '../types/common';
import { PageType } from './page-type';

export type BIRole = 'member' | 'owner' | 'visitor';

export enum CheckoutError {
  NO_PAYMENT_METHOD = 'NO_PAYMENT_METHOD',
  NO_RECURRING_PAYMENT_METHOD = 'NO_RECURRING_PAYMENT_METHOD',
  PLAN_ALREADY_PURCHASED = 'PLAN_ALREADY_PURCHASED',
  NO_PREMIUM = 'NO_PREMIUM',
}

export enum CheckoutAction {
  ApplyCoupon = 'apply_coupon',
  RemoveCoupon = 'remove_coupon',
  CheckTermsAndConditionsCheckbox = 'plan_policy_checkbox_agree',
  UncheckTermsAndConditionsCheckbox = 'plan_policy_checkbox_disagree',
  OpenTermsAndConditionsModal = 'plan_policy_read_click',
}

export const termsAndConditionsCheckboxAction = (newValue: boolean) => {
  return newValue ? CheckoutAction.CheckTermsAndConditionsCheckbox : CheckoutAction.UncheckTermsAndConditionsCheckbox;
};

export function userRoleToBIRole(role: UserData['role']): BIRole {
  switch (role) {
    default:
    case 'Member':
      return 'member';
    case 'Admin':
      return 'owner';
    case 'Visitor':
      return 'visitor';
  }
}

export function toMembershipStatus(pageType: PageType) {
  switch (pageType) {
    case PageType.DEFAULT_SUCCESS:
      return 'success';
    case PageType.VERTICAL_SUCCESS:
      return 'vertical_success';
    case PageType.ERROR:
      return 'error';
    default:
      return undefined;
  }
}

export function toBIPaymentType(recurring?: boolean) {
  return recurring ? 'recurring' : 'one-time';
}

export const SETTINGS_TABS = {
  display: 'display',
  layout: 'layout',
  plans: 'plans',
};

export const DISPLAY_VALUES = {
  enabled: 'enabled',
  disabled: 'disabled',
};

export const PLANS_TAB_FIELDS = {
  variantVisibility: 'variant_visibility',
  tabsViewPlanVisibility: 'tabs_view_plan_visibility',
};

export const WIDGET_TYPE = {
  list: 'list',
  page: 'page',
  mpaPage: 'mpa_page',
};

export const SUPPORT_LINK = {
  supportArticles: 'Read Support Articles',
  customerCare: 'Contact Customer Care',
};

export const SETTINGS_VIEW_MODE = {
  desktop: 'desktop',
  mobile: 'mobile',
};

export function benefitBulletTypeName(value: BenefitBulletType) {
  return benefitBulletTypeMap[value];
}

const benefitBulletTypeMap = {
  [BenefitBulletType.NONE]: 'none',
  [BenefitBulletType.CIRCLE_TICK]: 'circle_tick',
  [BenefitBulletType.HOLLOW_CIRLCE_TICK]: 'hollow_circle_tick',
  [BenefitBulletType.TICK]: 'tick',
  [BenefitBulletType.ARROW]: 'arrow',
};

export function reportClientAuditLog(
  biLogger: OwnerLogger | VisitorLogger | null | undefined,
  params: {
    entityType: string;
    entityId: string;
    message: string;
    data?: Record<string, any>;
    error?: unknown;
  },
) {
  if (!biLogger) {
    return;
  }

  const { entityType, entityId, message, data, error } = params;
  let errorData: Record<string, any> | undefined;
  if (error) {
    errorData = HttpClient.isHttpError(error)
      ? {
          message: error.message,
          requestId: error.requestId,
        }
      : { message: error instanceof Error ? error.message : new Error(String(error)).message };
  }
  void biLogger.report(
    pricingPlansClientAuditLogSrc92Evid408({
      entityType,
      entityId,
      message,
      data:
        data || errorData
          ? stringifySafe({
              ...data,
              error: errorData,
            })
          : undefined,
    }),
  );
}

export function reportAppStateFix(
  biLogger: OwnerLogger | VisitorLogger | null | undefined,
  message: string,
  data?: Record<string, any>,
) {
  reportClientAuditLog(biLogger, {
    entityType: 'app-state-fix',
    entityId: '',
    message,
    data,
  });
}
